<template>
  <v-container fluid>
    <v-row align="start" justify="start" class="devices">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="displayDevices"
          :loading="pageLoading"
          :server-items-length="totalCount"
          @update:options="paginate($event)"
          :footer-props="footerProps"
          :options="{
            page: pageIndex,
            itemsPerPage: pageSize,
            sortBy: sort.sortBy,
            sortDesc: sort.sortDesc,
          }"
          multi-sort
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title class="d-flex align-center">
                {{ $localizationService.localize("device_page.title") }}
                <TableSettings
                  class="ml-5"
                  :pIsVisible="tableInited && !isDefaultSettings"
                  :pSaveButtonTooltip="$localizationService.localize('device_page.settings_tooltip.save')"
                  :pDefaultButtonTooltip=
                    "$localizationService.localize('device_page.settings_tooltip.apply_default')"
                  @onSaveSettings="saveSettings()"
                  @onApplyDefaultSettings="applyDefaultSettings()"
                />
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="searchString"
                :label="$localizationService.localize('device_page.search')"
                class="search-field"
                hide-details
                @input="onInputSearchString()"
              ></v-text-field>
              <v-btn
                color="primary"
                class="ml-2"
                @click="openEditDeviceDialog()"
              >
                {{ $localizationService.localize("device_page.new_btn") }}
              </v-btn>
              <EditDeviceDialog ref="devicesDialog" />
              <ConfirmationDialog ref="deleteDialog" />
            </v-toolbar>
          </template>

          <template v-for="header in headers" v-slot:[`header.${header.value}`]>
            <span
              :key="header.value"
              :class="{
              'not-default-header-sort': isNotDefaultSortItems.includes(header.value)
              }"
            >
              {{ header.text }}
            </span>
          </template>

          <template v-slot:[`item.name`]="{ item }">
            <div class="d-flex align-center">
              <span>{{ item.name }}</span>
              <MessageButton
                class="ml-1"
                :target="MessagingTargets.device(item.id)"
              />
            </div>
          </template>

          <template v-slot:[`item.number`]="{ item }">
            {{ item.number }}
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            {{ momentDateAndTime(item.createdAt) }}
          </template>

          <template v-slot:[`item.updatedAt`]="{ item }">
            {{ momentDateAndTime(item.updatedAt) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-icon medium class="mr-2" @click="openEditDeviceDialog(item)"
              >mdi-pencil</v-icon
            >
            <v-icon medium color="red" @click="openDeleteDeviceDialog(item)"
              >mdi-delete</v-icon
            >
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import lodash from "lodash";
import { MessagingTargets, momentDateAndTime, paginateTable } from "../../utils/utils";
import tableSortParserMixin from "@/utils/table-sort-parser";

const locService = Vue.prototype.$localizationService;

export default {
  name: "Devices",

  mixins: [tableSortParserMixin],

  components: {
    EditDeviceDialog: () => import("./EditDeviceDialog.vue"),
    ConfirmationDialog: () => import("@/components/ConfirmationDialog.vue"),
    MessageButton: () => import("@/components/MessageButton.vue"),
    TableSettings: () => import("@/components/TableSettings.vue"),
  },

  data: () => ({
    MessagingTargets,
    localStorageTable: "devices.table.size",
    tableInited: false,

    footerProps: {
      "items-per-page-options": [25, 50, 100],
    },

    searchString: "",

    headers: [
      {
        text: locService.localize("device_page.header.name"),
        value: "name",
        sortable: true,
      },
      {
        text: locService.localize("device_page.header.number"),
        value: "number",
        sortable: true,
      },
      {
        text: locService.localize("device_page.header.created_at"),
        value: "createdAt",
        sortable: true,
      },
      {
        text: locService.localize("device_page.header.updated_at"),
        value: "updatedAt",
        sortable: true,
      },
      {
        text: locService.localize("device_page.header.actions"),
        value: "actions",
        align: "end",
        sortable: false,
      },
    ],
  }),
  computed: {
    ...mapState("devicesModule", [
      "displayDevices",
      "totalCount",
      "pageLoading",
      "currentPage",
    ]),
    ...mapState("devicesTableSettingsModule", [
      "pageIndex",
      "pageSize",
      "sort",
      "defaultSettings",
    ]),
    ...mapGetters("devicesTableSettingsModule", [
      "isDefaultSettings",
      "isDefaultSort",
      "isDefaultSize",
    ]),

    isNotDefaultSortItems() {
      return !this.isDefaultSort
        ? this.getNotDefaultTableSortItems("sort")
        : [];
    },
  },
  methods: {
    momentDateAndTime,

    paginate(data) {
      data.itemsPerPage = paginateTable(
        data,
        this.localStorageTable,
        this.tableInited,
      );
      const currentPage = {
        index: data.page - 1,
        size: data.itemsPerPage,
        sort: data.sortBy.map((it, i) => (data.sortDesc[i] ? `-${it}` : it)),
        searchString: this.searchString,
      };

      this.$store.commit("devicesTableSettingsModule/changeSettings", {
        index: data.page,
        size: data.itemsPerPage,
        sort: {
          sortBy: [...data.sortBy],
          sortDesc: [...data.sortDesc],
        },
      });

      this.updateRoutePath();

      this.$store.dispatch("devicesModule/loadPage", currentPage);
      this.tableInited = true;
    },

    updateRoutePath() {
      const params = new URLSearchParams();

      params.append("pageSize", this.pageSize);

      params.append("pageIndex", this.pageIndex);

      params.append("sort", JSON.stringify(this.sort));

      if (this.searchString) {
        params.append("searchString", this.searchString);
      }

      if (
        this.$route.query.pageSize != (params.get("pageSize") || undefined)
          || this.$route.query.pageIndex != (params.get("pageIndex") || undefined)
          || this.$route.query.searchString != (params.get("searchString") || undefined)
          || this.$route.query.sort != (params.get("sort") || undefined)
      ) {
        this.$router.push(`/devices/?${params.toString()}`);
      }
    },

    onInputSearchString: lodash.debounce(function () {
      const newPage = { ...this.currentPage };

      newPage.searchString = this.searchString;

      this.$store.dispatch("devicesModule/loadPage", newPage);
      this.updateRoutePath();
    }, 600),

    saveSettings() {
      this.$store.commit("devicesTableSettingsModule/saveSettings");

      Vue.toasted.success(
        locService.localize("devices_page.settings_msg.save"),
        { duration: 3000 },
      );
    },

    applyDefaultSettings() {
      this.$store.commit("devicesTableSettingsModule/applyDefaultSettings");
    },

    openEditDeviceDialog(device) {
      this.$refs.devicesDialog.openDialog(device);
    },

    openDeleteDeviceDialog(device) {
      this.$refs.deleteDialog.openDialog({
        header: locService.localize("device_page.dialog.delete.title"),
        text: locService.localize("device_page.dialog.delete.text", [
          device.name,
        ]),
        submitText: locService.localize("btn.delete"),
        submitColor: "red",
        onSubmit: async () => this.$store.dispatch("devicesModule/deleteDevice", device),
      });
    },
  },

  created() {
    this.$store.dispatch("devicesTableSettingsModule/loadDefaultSettingsFromStorage");

    const query = this.$route.query || {};

    if (!Object.keys(query).length) {
      this.$store.commit("devicesTableSettingsModule/applyDefaultSettings");
    } else {
      this.$store.commit("devicesTableSettingsModule/applyQuerySettings", query);

      if (query.searchString) {
        this.searchString = query.searchString;
      }
    }
  },
};
</script>

<style scoped lang="scss">
.devices {
  .v-data-table-header-mobile {
    display: none;
  }
}
.search-field {
  max-width: 250px;
}
</style>
